/**
 * `P0-DLW-018`, round F: **what a permission LEVEL opens, derived from the
 * record's own definitions and not from the absence of a key.**
 *
 * ## The number is 018 and the architect's ruling said 012
 *
 * `F1`'s ruling minted this as `P0-DLW-012`. **That number was taken**:
 * `P2-DLW-012` is *"Vehicle handover against the buyer's bon de retrait"*, and
 * `M0`'s Condition 1(b) settled exactly this question , *the identity of a
 * series is the module's; the priority prefix is a property of the item, not of
 * the series* , proven there with `P0-IDN-070` / `P1-IDN-071` / `P0-IDN-072`.
 * Two items numbered `DLW-012` would be the collision that ruling forbids.
 *
 * The highest `DLW` in the plan is `P2-DLW-017`, so this is `018`. Renumbered
 * everywhere it appears rather than left to be discovered, and reported.
 *
 * ## The defect this replaces, and why its cure was green for the wrong reason
 *
 * `P0-DLW-007` cured the founder's step-6 finding , the navigation offered
 * « Équipe » to an employee, who pressed it and read the door's refusal , by
 * filtering the rail on `rights[entry.right] !== undefined`.
 *
 * **`/me` carries a right the caller does NOT hold.** Read from the live door
 * as an employee on the e2e tier:
 *
 *     "permissions": { "gererEquipeConcession": "non", ..., "gererInventaire": "oui" }
 *
 * So the key is present with the value `"non"`, `!== undefined` is true, and
 * **the entry renders**. The defect the walk found was never cured; it was
 * hidden by a fixture. `team.spec.tsx`'s employee carried `permissions: {}`
 * with the comment *"the employee's `permissions` map simply does not carry
 * it"* , the client's expectation, not the provider's contract. That is `A48`
 * exactly, and `§5.33`: a control green for the wrong reason.
 *
 * ## The four levels, from 06 §6.1.7's own table
 *
 * | level | the record's words |
 * |---|---|
 * | `oui` | *"Allowed within the caller's scope (6.1.8)"* |
 * | `non` | *"Refused. **The control is not rendered**, and the route answers `403 forbidden`"* |
 * | `trace` | *"Allowed, and every exercise writes an `audit.audit_events` row, including a read"* |
 * | `surAccord` | *"Allowed **only** with the partner's recorded consent, and always audited"* |
 *
 * **The derivation, which is not « everything but `non` ».**
 *
 * - `non` closes, and the record says so in the words *"the control is not
 *   rendered"* , this function is that sentence.
 * - `oui` opens.
 * - `trace` opens: it is *allowed*, and the audit row is the api's obligation
 *   when the door is used, not a condition on offering it.
 * - **`surAccord` does NOT open on its own.** It is allowed *only* with a
 *   recorded consent that lives in `partnerships.workspace_consents`, and this
 *   app cannot see whether one exists. Rendering it would offer a gesture the
 *   api may refuse , `R77`'s rule , so it is treated as closed here. Nothing is
 *   lost today: 06 §6.1.7 says *"Entering a partner's workspace is the only
 *   `surAccord` right today"*, and it is Hakili staff's, never a dealer's. The
 *   day a dealer right becomes `surAccord`, this function must learn to read
 *   the consent rather than guess, and it will fail closed until it does.
 *
 * An unknown level fails CLOSED for the same reason: a level this build has not
 * been taught is a level whose meaning it cannot honour.
 */

/** The levels the record defines. Anything else is a level this build has not met. */
export const OPENING_LEVELS: ReadonlySet<string> = new Set(["oui", "trace"]);

/**
 * Whether a caller holding this level may be OFFERED the control.
 *
 * The api's refusal remains the enforcement , hiding a control is a courtesy,
 * and every route still answers in its own words to anyone who reaches it by
 * address (`P0-DLW-007`'s own rule, unchanged).
 */
export function opensControl(level: string | undefined): boolean {
  return level !== undefined && OPENING_LEVELS.has(level);
}
